import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { SearchResults } from '#src/app/pages/search/shared/api';
import { useSearchClientContext } from '#src/app/pages/search/shared/searchClientProvider';

import { useSearchState } from '../useSearchState';

// Search results are short-lived but worth caching for back/forward and repeat queries. These
// per-query options deliberately override the global no-cache defaults (gcTime/staleTime: 0,
// refetchOnMount: 'always') so cached results aren't discarded/refetched on every navigation.
const STALE_TIME_MS = 120_000;
const GC_TIME_MS = 5 * 60_000;

/**
 * Results store, backed by react-query. Every caller shares one fetch + one cache entry keyed on
 * the URL-derived params, and re-renders only when results change (not when unrelated filters do).
 */
export function useSearchResults(): SearchResults {
	const { searchClient, collectionName } = useSearchClientContext();
	const params = useSearchState();

	const query = useQuery({
		queryKey: ['search-petitions', collectionName, params],
		queryFn: async () => searchClient.searchPetitions(params),
		placeholderData: keepPreviousData,
		staleTime: STALE_TIME_MS,
		gcTime: GC_TIME_MS,
		refetchOnMount: false,
	});

	return {
		hits: query.data?.hits ?? [],
		nbHits: query.data?.nbHits ?? 0,
		nbPages: query.data?.nbPages ?? 0,
		query: query.data?.query ?? '',
		topicFacets: query.data?.topicFacets ?? [],
		isLoading: query.isFetching,
		isError: query.isError,
		isInitialLoad: query.isLoading,
	};
}
